import { BATTLE_ELEMENTS, CARD_HEIGHT, CARD_WIDTH, photoFrameSize, photoSourceRect, trimCardText, type CardIdentity, type CardResult, type CardStyle, type PhotoCrop } from './core';

export interface CardDrawing {
  style: CardStyle;
  identity: CardIdentity;
  result: CardResult;
  photo: CanvasImageSource | null;
  photoWidth: number;
  photoHeight: number;
  crop: PhotoCrop;
}

const FONT = '"Noto Sans KR", sans-serif';
const LATIN = '"Lexend Deca", "Noto Sans KR", sans-serif';
type Context = CanvasRenderingContext2D;

function box(ctx: Context, x: number, y: number, w: number, h: number, r: number, fill: string | CanvasGradient, stroke?: string, line = 2) {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
  ctx.fillStyle = fill;
  ctx.fill();
  if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = line; ctx.stroke(); }
}

function text(ctx: Context, value: string, x: number, y: number, size: number, color: string, weight = 700, maxWidth?: number, family = FONT) {
  ctx.fillStyle = color;
  ctx.font = weight + ' ' + size + 'px ' + family;
  if (maxWidth) {
    while (ctx.measureText(value).width > maxWidth && size > 24) {
      size -= 1;
      ctx.font = weight + ' ' + size + 'px ' + family;
    }
  }
  ctx.fillText(value, x, y);
}

function gradient(ctx: Context, x: number, y: number, w: number, h: number, colors: string[]) {
  const g = ctx.createLinearGradient(x, y, x + w, y + h);
  colors.forEach((color, index) => g.addColorStop(index / (colors.length - 1), color));
  return g;
}

function line(ctx: Context, x: number, y: number, x2: number, y2: number, color: string, width = 2) {
  ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x2, y2); ctx.strokeStyle = color; ctx.lineWidth = width; ctx.stroke();
}

function sparkle(ctx: Context, x: number, y: number, radius: number, color: string) {
  ctx.beginPath();
  ctx.moveTo(x, y - radius); ctx.quadraticCurveTo(x + radius * .16, y - radius * .16, x + radius, y);
  ctx.quadraticCurveTo(x + radius * .16, y + radius * .16, x, y + radius);
  ctx.quadraticCurveTo(x - radius * .16, y + radius * .16, x - radius, y);
  ctx.quadraticCurveTo(x - radius * .16, y - radius * .16, x, y - radius);
  ctx.fillStyle = color; ctx.fill();
}

function photo(ctx: Context, drawing: CardDrawing, x: number, y: number, radius: number) {
  const { width: w, height: h } = photoFrameSize(drawing.style);
  ctx.save();
  ctx.beginPath(); ctx.roundRect(x, y, w, h, radius); ctx.clip();
  ctx.fillStyle = '#d6c9ec'; ctx.fillRect(x, y, w, h);
  if (drawing.photo && drawing.photoWidth > 0 && drawing.photoHeight > 0) {
    const crop = photoSourceRect(drawing.photoWidth, drawing.photoHeight, w, h, drawing.crop);
    ctx.drawImage(drawing.photo, crop.sx, crop.sy, crop.sw, crop.sh, x, y, w, h);
  } else {
    text(ctx, 'YOUR PHOTO', x + 44, y + h / 2, 48, '#665583', 700, w - 88, LATIN);
  }
  ctx.restore();
}

function skillLines(ctx: Context, value: string, x: number, y: number, width: number, color: string, size = 32) {
  ctx.font = '600 ' + size + 'px ' + FONT;
  const chars = Array.from(new Intl.Segmenter('ko', { granularity: 'grapheme' }).segment(value), (part) => part.segment);
  let current = ''; let row = 0;
  for (const char of chars) {
    if (ctx.measureText(current + char).width > width && current) {
      text(ctx, current, x, y + row * (size + 16), size, color, 600);
      current = char; row += 1;
    } else current += char;
  }
  text(ctx, current, x, y + row * (size + 16), size, color, 600);
}

function holo(ctx: Context, d: CardDrawing) {
  const { result: r, identity: i } = d;
  box(ctx, 0, 0, 900, 1260, 48, '#181620');
  const foil = gradient(ctx, 0, 0, 900, 1260, ['#cf9af8', '#d0f9e8', '#f8e3af', '#b5bcff', '#f4b5d5', '#b8f1db']);
  box(ctx, 13, 13, 874, 1234, 40, foil);
  box(ctx, 26, 26, 848, 1208, 30, '#201c2c');
  text(ctx, 'ONEUL / CHARACTER COLLECTION', 62, 78, 24, '#c4bdd6', 500, 670, LATIN);
  text(ctx, trimCardText(i.name, 12) || '이름 없는 주인공', 62, 157, 67, '#fff', 800, 604);
  box(ctx, 722, 109, 112, 58, 29, foil);
  text(ctx, r.rarity, 744, 151, 33, '#282038', 800, 80, LATIN);
  text(ctx, r.title, 64, 202, 29, '#d8c6f5', 500, 640);
  photo(ctx, d, 60, 230, 16);
  const shade = ctx.createLinearGradient(0, 700, 0, 870);
  shade.addColorStop(0, '#1c182400'); shade.addColorStop(1, '#1c1824b3');
  box(ctx, 60, 700, 780, 170, 0, shade);
  text(ctx, 'ONE OF A KIND', 92, 831, 25, '#fff', 700, 600, LATIN);
  sparkle(ctx, 785, 812, 27, '#f0d4fe');
  for (let n = 0; n < 3; n += 1) {
    const x = 65 + n * 263;
    text(ctx, r.labels[n], x, 925, 26, '#b4aec4', 500);
    text(ctx, String(r.stats[n]).padStart(2, '0'), x, 993, 58, '#faf5ff', 700, 190, LATIN);
    line(ctx, x, 1013, x + 217, 1013, '#494154', 6);
    line(ctx, x, 1013, x + 217 * r.stats[n] / 99, 1013, ['#b8e9d8', '#e2c0f9', '#f4dba6'][n], 6);
  }
  text(ctx, 'SPECIAL ABILITY', 65, 1075, 23, '#c7a8ea', 700, undefined, LATIN);
  skillLines(ctx, trimCardText(i.skill, 30) || r.defaultSkill, 65, 1122, 760, '#fff', 31);
  line(ctx, 65, 1190, 834, 1190, '#4d405b');
  text(ctx, 'NO. ' + r.serial, 65, 1221, 21, '#b4a9c5', 500, undefined, LATIN);
  text(ctx, '오늘툴 · oneultool.com', 592, 1221, 21, '#b4a9c5', 500);
}

function checker(ctx: Context, x: number, y: number, width: number, size: number, color: string) {
  ctx.fillStyle = color;
  for (let col = 0; col < Math.ceil(width / size); col += 1) {
    for (let row = 0; row < 2; row += 1) if ((col + row) % 2 === 0) ctx.fillRect(x + col * size, y + row * size, Math.min(size, width - col * size), size);
  }
}

function retro(ctx: Context, d: CardDrawing) {
  const { result: r, identity: i } = d;
  box(ctx, 0, 0, 900, 1260, 38, '#242626');
  box(ctx, 15, 15, 870, 1230, 28, '#f6efcf');
  box(ctx, 39, 39, 822, 1182, 12, '#f6efcf', '#252825', 4);
  box(ctx, 39, 39, 822, 76, 10, '#285ccc');
  text(ctx, 'PLAYER / 01', 62, 91, 32, '#fff5d0', 700, undefined, LATIN);
  text(ctx, r.rarity + ' EDITION', 643, 90, 29, '#fff5d0', 700, 190, LATIN);
  text(ctx, trimCardText(i.name, 12) || '이름 없는 주인공', 63, 192, 67, '#242626', 900, 770);
  text(ctx, r.title, 65, 239, 27, '#675c45', 500, 720);
  box(ctx, 61, 264, 638, 598, 1, '#262a29');
  photo(ctx, d, 69, 272, 0);
  box(ctx, 718, 264, 118, 598, 2, '#e44e37');
  ctx.save(); ctx.translate(792, 306); ctx.rotate(Math.PI / 2);
  text(ctx, 'READY, PLAYER ME!', 0, 0, 37, '#fff4cb', 800, 515, LATIN); ctx.restore();
  text(ctx, '+', 743, 815, 65, '#fff4cb', 800, undefined, LATIN);
  checker(ctx, 61, 881, 775, 16, '#242626');
  for (let n = 0; n < 3; n += 1) {
    const x = 65 + n * 261;
    text(ctx, r.labels[n], x, 964, 27, '#655e4b', 600);
    text(ctx, String(r.stats[n]).padStart(2, '0'), x, 1026, 59, '#252b29', 800, undefined, LATIN);
    if (n < 2) line(ctx, x + 227, 938, x + 227, 1031, '#c3bda7');
  }
  box(ctx, 62, 1052, 774, 119, 0, '#e4dbb2');
  text(ctx, '특수기', 79, 1092, 25, '#285ccc', 800);
  skillLines(ctx, trimCardText(i.skill, 30) || r.defaultSkill, 180, 1092, 635, '#242626', 30);
  text(ctx, 'NO. ' + r.serial, 64, 1202, 20, '#655e4b', 500, undefined, LATIN);
  text(ctx, '오늘툴 · oneultool.com', 594, 1202, 20, '#655e4b', 500);
}

function sticker(ctx: Context, d: CardDrawing) {
  const { result: r, identity: i } = d;
  box(ctx, 0, 0, 900, 1260, 42, '#393045');
  box(ctx, 10, 10, 880, 1240, 34, '#efd3ee');
  ctx.save(); ctx.beginPath(); ctx.roundRect(10, 10, 880, 1240, 34); ctx.clip();
  for (let p = 42; p < 1260; p += 48) line(ctx, 10, p, 890, p, '#d4b4d350');
  for (let p = 42; p < 900; p += 48) line(ctx, p, 10, p, 1250, '#d4b4d350');
  ctx.restore();
  text(ctx, 'ME, MYSELF', 51, 101, 65, '#423046', 900, 778, LATIN);
  text(ctx, '& MY LITTLE WORLD.', 55, 149, 28, '#6e4675', 600, 730, LATIN);
  ctx.save(); ctx.translate(445, 540); ctx.rotate(-.042);
  box(ctx, -350 + 9, -331 + 12, 714, 730, 5, '#ae7fa955');
  box(ctx, -350, -331, 714, 730, 5, '#fffcf5');
  photo(ctx, d, -326, -307, 0);
  text(ctx, trimCardText(i.name, 12) || '이름 없는 주인공', -307, 345, 59, '#433347', 800, 630);
  ctx.restore();
  ctx.save(); ctx.translate(706, 226); ctx.rotate(.15);
  box(ctx, -102, -40, 207, 80, 40, '#d5f068', '#40354a', 3);
  text(ctx, r.rarity + ' CLUB', -76, 12, 31, '#40354a', 800, undefined, LATIN); ctx.restore();
  sparkle(ctx, 97, 805, 48, '#285ccc');
  sparkle(ctx, 807, 823, 37, '#e75b3c');
  box(ctx, 72, 940, 756, 91, 45, '#d5f068', '#40354a', 3);
  for (let n = 0; n < 3; n += 1) {
    const x = 100 + n * 250;
    text(ctx, r.labels[n], x, 978, 24, '#4f5140', 600);
    text(ctx, String(r.stats[n]), x + 122, 1000, 44, '#352b3f', 800, 80, LATIN);
  }
  text(ctx, r.title, 65, 1091, 31, '#533253', 800, 775);
  skillLines(ctx, trimCardText(i.skill, 30) || r.defaultSkill, 65, 1137, 775, '#654467', 30);
  line(ctx, 65, 1192, 835, 1192, '#b38faf');
  text(ctx, 'NO. ' + r.serial, 65, 1225, 21, '#79597c', 500, undefined, LATIN);
  text(ctx, '오늘툴 · oneultool.com', 592, 1225, 21, '#79597c', 500);
}

function star(ctx: Context, x: number, y: number, radius: number) {
  ctx.beginPath();
  for (let n = 0; n < 10; n += 1) {
    const angle = -Math.PI / 2 + n * Math.PI / 5;
    const r = n % 2 ? radius * .46 : radius;
    const px = x + Math.cos(angle) * r; const py = y + Math.sin(angle) * r;
    if (n === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
  }
  ctx.closePath(); ctx.fillStyle = '#ffdc55'; ctx.strokeStyle = '#61451d'; ctx.lineWidth = 3;
  ctx.fill(); ctx.stroke();
}

function paragraph(ctx: Context, value: string, x: number, y: number, width: number, size: number, rows: number, color: string) {
  const chars = Array.from(new Intl.Segmenter('ko', { granularity: 'grapheme' }).segment(value), (part) => part.segment);
  let lines: string[] = [];
  for (;;) {
    ctx.font = '500 ' + size + 'px ' + FONT;
    lines = [''];
    for (const char of chars) {
      if (lines.at(-1) && ctx.measureText(lines.at(-1) + char).width > width) lines.push(char);
      else lines[lines.length - 1] += char;
    }
    if (lines.length <= rows || size <= 20) break;
    size -= 1;
  }
  if (lines.length > rows) {
    let last = Array.from(new Intl.Segmenter('ko', { granularity: 'grapheme' }).segment(lines[rows - 1]), (part) => part.segment);
    while (ctx.measureText(last.join('') + '…').width > width && last.length) last.pop();
    lines[rows - 1] = last.join('') + '…';
  }
  lines.slice(0, rows).forEach((value, index) => text(ctx, value.trim(), x, y + index * (size + 9), size, color, 500));
}

function battle(ctx: Context, d: CardDrawing) {
  const { result: r, identity: i } = d; const b = r.battle;
  const palette = BATTLE_ELEMENTS.find((item) => item.id === (i.element || b.element))!;
  const metal = gradient(ctx, 0, 0, 900, 1260, ['#7c8795', '#fcffff', '#a1acb7', '#f7fbff', '#85909f']);
  box(ctx, 0, 0, 900, 1260, 40, '#283344');
  box(ctx, 10, 10, 880, 1240, 32, metal);
  box(ctx, 25, 25, 850, 1210, 24, gradient(ctx, 0, 0, 900, 1260, [palette.pale, palette.color, palette.pale]), palette.dark, 3);
  box(ctx, 40, 39, 820, 1178, 17, gradient(ctx, 0, 0, 400, 1260, [palette.pale, '#fffef5', palette.pale]));
  box(ctx, 48, 42, 804, 52, 12, palette.dark);
  text(ctx, 'ONEUL / STAR BATTLE', 68, 77, 23, '#fff', 700, 490, LATIN);
  text(ctx, 'HP ' + b.hp, 701, 78, 27, '#fff', 700, 140, LATIN);
  text(ctx, trimCardText(i.name, 12) || '이름 없는 주인공', 65, 148, 57, palette.dark, 800, 615);
  box(ctx, 719, 109, 119, 51, 25, gradient(ctx, 0, 0, 100, 80, ['#fff9df', '#ffcf52']), '#9d7628', 2);
  text(ctx, r.rarity, 745, 145, 32, '#4e3b22', 800, 78, LATIN);
  text(ctx, r.title + ' · Lv.' + b.level, 66, 188, 25, palette.dark, 600, 480);
  for (let n = 0; n < b.stars; n += 1) star(ctx, 826 - n * 42, 184, 19);
  box(ctx, 55, 204, 790, 452, 19, metal, '#5e7183', 3);
  photo(ctx, d, 64, 210, 12);
  box(ctx, 73, 222, 100, 38, 19, palette.dark);
  text(ctx, palette.title + ' 속성', 86, 249, 21, '#fff', 600, 79);
  const shade = ctx.createLinearGradient(0, 578, 0, 650);
  shade.addColorStop(0, '#12203300'); shade.addColorStop(1, '#122033b8');
  box(ctx, 64, 578, 772, 72, 0, shade);
  text(ctx, ({ daily: '일상계', office: '직장인계', pet: '반려동물계' })[i.theme] + ' / 유일무이한 캐릭터', 88, 629, 24, '#fff', 600);
  box(ctx, 57, 674, 786, 77, 28, metal, '#73808f', 2);
  box(ctx, 64, 681, 772, 63, 22, gradient(ctx, 0, 681, 0, 63, ['#f8fcff', '#c4cdd5', '#fafdff']));
  text(ctx, 'ATK / ' + b.attack, 99, 726, 37, '#273a4c', 700, 310, LATIN);
  line(ctx, 450, 688, 450, 737, '#99a7b4', 2);
  text(ctx, 'DEF / ' + b.defense, 496, 726, 37, '#273a4c', 700, 310, LATIN);
  box(ctx, 57, 769, 786, 137, 14, '#ffffffb8', palette.color, 1.5);
  box(ctx, 75, 785, 64, 32, 8, palette.dark);
  text(ctx, '특성', 85, 808, 21, '#fff', 700);
  text(ctx, b.passive.name, 155, 812, 30, palette.dark, 800, 650);
  paragraph(ctx, trimCardText(i.skill, 30) || b.passive.description, 77, 852, 744, 26, 2, '#38414a');
  box(ctx, 57, 919, 786, 138, 14, '#ffffffb8', palette.color, 1.5);
  for (let n = 0; n < b.move.cost; n += 1) {
    box(ctx, 76 + n * 29, 939, 23, 23, 12, palette.color, palette.dark, 1);
  }
  text(ctx, b.move.name, 214, 963, 33, palette.dark, 800, 454);
  text(ctx, String(b.move.power), 735, 964, 41, palette.dark, 800, 88, LATIN);
  paragraph(ctx, b.move.description, 77, 1001, 744, 26, 2, '#38414a');
  text(ctx, '약점  ' + b.weakness, 74, 1092, 25, palette.dark, 600, 365);
  text(ctx, '회복  ' + b.resistance, 477, 1092, 25, palette.dark, 600, 355);
  line(ctx, 66, 1108, 834, 1108, palette.color, 1.5);
  paragraph(ctx, trimCardText(i.story || '', 70) || b.story, 73, 1137, 751, 22, 3, '#4d5258');
  text(ctx, 'NO. ' + r.serial + ' · ' + b.stars + ' STAR', 64, 1224, 20, palette.dark, 600, 470, LATIN);
  text(ctx, '오늘툴 · oneultool.com', 594, 1224, 20, palette.dark, 500);
}

export function drawCard(ctx: Context, drawing: CardDrawing): void {
  ctx.save();
  ctx.clearRect(0, 0, CARD_WIDTH, CARD_HEIGHT);
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  if (drawing.style === 'battle') battle(ctx, drawing);
  else if (drawing.style === 'retro') retro(ctx, drawing);
  else if (drawing.style === 'sticker') sticker(ctx, drawing);
  else holo(ctx, drawing);
  ctx.restore();
}
